import React from 'react'
import { Button, Checkbox, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@carsys/ui'
import { useMutation } from '@cbgms/base/http/use-query'
import { useDispatch } from 'react-redux'
import {
  logVinUnmaskByRegistration as logVinUnmaskByRegistrationAction,
  logVinUnmaskDialogOpen,
  showVehicleVin,
  showVehicleVinByRegistration
} from '../state/actions'
import { useFeature } from '@cbgms/base/feature-flags'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import { VehicleProvider } from '@cbgms/api'
import { useLocation } from 'react-router-dom'
import { ResourceTypePayload } from '@cbgms/api/modules/vehicles'
import { getResourceType } from '../helpers/getResourceType'
interface UseVinUnmaskParams {
  vin: string
  vehicleUUID?: string
  workorderUUID?: string
  registration?: string
  resourceID?: string | null
}

interface UseVinUnmaskReturn {
  vinMasked: () => boolean
  unmaskVin: (vin?: string) => Promise<void>
  unmaskVinByRegistration: (registration: string, provider?: string) => Promise<string | null>
  isUnmasking: boolean
  unmaskedVin: string | null
  UnmaskConfirmationDialog: React.FC<UnmaskConfirmationDialogProps>
  vinMaskingEnabled: boolean
  isVinMasked: (vin: string) => boolean
  resetUnmaskedVin: () => void
  resourceType: string | undefined
  resourceID: string | undefined
}

export interface UnmaskConfirmationDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  vehicleUUID?: string
  workorderUUID?: string
  registration?: string
  hasVin?: boolean
  resourceInfo?: ResourceTypePayload
}

const isVinMasked = (vin: string): boolean => {
  if (!vin) return false
  return vin.startsWith('******')
}

export const VinUnmaskConfirmationDialog: React.FC<UnmaskConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  vehicleUUID,
  workorderUUID,
  registration,
  hasVin,
  resourceInfo
}) => {
  const { t } = useTranslation()
  const [agreed, setAgreed] = React.useState(false)
  const dispatch = useDispatch()
  const hasLoggedRef = React.useRef(false)

  React.useEffect(() => {
    if (open && vehicleUUID && !hasLoggedRef.current) {
      // Log the appropriate event based on whether we have a VIN or need to unmask by registration
      const logDialogOpen = async (provider = VehicleProvider.Mdm) => {
        try {
          if (hasVin) {
            // VIN exists, log dialog open for unmasking existing VIN
            await dispatch(logVinUnmaskDialogOpen(vehicleUUID, workorderUUID, resourceInfo))
          } else if (registration) {
            // No VIN, will log dialog open for unmasking by registration
            await dispatch(logVinUnmaskByRegistrationAction(vehicleUUID, registration, provider, resourceInfo))
          }
          hasLoggedRef.current = true
        } catch (error) {
          console.error('Failed to log VIN unmask event:', error)
        }
      }
      logDialogOpen()
    }
    if (!open) {
      hasLoggedRef.current = false
    }
  }, [open, vehicleUUID, workorderUUID, registration, hasVin, dispatch, resourceInfo])

  React.useEffect(() => {
    if (!open) {
      setAgreed(false)
    }
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} enableBackdropClick>
      <DialogTitle>{t('Vehicles.VinUnmask.ConfirmationDialog.Title')}</DialogTitle>
      <DialogContent>
        <DialogContentText
          dangerouslySetInnerHTML={{
            __html: t('Vehicles.VinUnmask.ConfirmationDialog.Description')
          }}
        />
      </DialogContent>
      <DialogActions>
        <FormControlLabel
          control={<Checkbox checked={agreed} onChange={e => setAgreed(e.target.checked)} color='primary' />}
          label={undefined}
        />
        <Button onClick={onConfirm} color='primary' variant='contained' disabled={!agreed} autoFocus>
          {t('Vehicles.VinUnmask.ConfirmationDialog.Confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Confirmation dialog component for VIN save confirmation
 * This dialog asks users if they want to save the VIN to the database
 */
export const VinSaveConfirmationDialog: React.FC<UnmaskConfirmationDialogProps> = ({ open, onClose, onConfirm }) => {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('Vehicles.VinConfirmation.Title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('Vehicles.VinConfirmation.Description')}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary' variant='outlined'>
          {t('Vehicles.VinConfirmation.No')}
        </Button>
        <Button onClick={onConfirm} color='primary' variant='contained' autoFocus>
          {t('Vehicles.VinConfirmation.Yes')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
/**
 * Custom hook for managing VIN masking/unmasking functionality with UK privacy compliance
 *
 * This hook handles VIN masking compliance with UK privacy laws where VINs
 * cannot be shown publicly without user consent. It provides:
 * - Detection of masked VINs (starting with ******)
 * - API call functionality to unmask VINs with proper authorization
 * - Confirmation dialog component for user consent
 * - Feature flag and portal mode checks
 *
 * The feature is only active when:
 * - Feature flag 'uk-vin-masking' is enabled
 * - User is NOT in portal mode
 *
 * @param params - Object containing vin, vehicleUUID, and optional workorderUUID
 * @returns Object containing vinMasked flag, unmaskVin function, loading state, unmasked VIN, dialog component, and feature enabled flag
 *
 * @example
 * // In a read-only context (e.g., dashboard)
 * ```tsx
 * const { vinMasked, unmaskVin, UnmaskConfirmationDialog } = useVinUnmask({
 *   vin: vehicle.VIN,
 *   vehicleUUID: vehicle.UUID,
 *   workorderUUID: workorder.UUID
 * })
 *
 * const [dialogOpen, setDialogOpen] = useState(false)
 *
 * return (
 *   <>
 *     {vinMasked && (
 *       <Button onClick={() => setDialogOpen(true)}>
 *         Show Full VIN
 *       </Button>
 *     )}
 *     <UnmaskConfirmationDialog
 *       open={dialogOpen}
 *       onClose={() => setDialogOpen(false)}
 *       onConfirm={() => {
 *         unmaskVin()
 *         setDialogOpen(false)
 *       }}
 *     />
 *   </>
 * )
 * ```
 *
 * @example
 * // In an editable context (e.g., vehicle form)
 * ```tsx
 * const { vinMaskingEnabled, vinMasked } = useVinUnmask({
 *   vin: currentVin,
 *   vehicleUUID: vehicle.UUID
 * })
 *
 * return (
 *   <TextField
 *     name="vin"
 *     disabled={vinMaskingEnabled ? !editEnabled : false}
 *     InputProps={{
 *       endAdornment: vinMaskingEnabled && !vinMasked && (
 *         <IconButton onClick={handleEdit}>
 *           <EditIcon />
 *         </IconButton>
 *       )
 *     }}
 *   />
 * )
 * ```
 */
export const useVinUnmask = ({ vin, vehicleUUID, workorderUUID, registration, resourceID }: UseVinUnmaskParams): UseVinUnmaskReturn => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [unmaskedVin, setUnmaskedVin] = React.useState<string | null>(null)
  const hasVinMaskingFeature = useFeature('uk-vin-masking')
  const isPortal = useIsPortal()

  // Calculate resource type and ID based on current navigation
  const resourceInfo = React.useMemo(() => {
    const id = resourceID || ''
    const fullUrl = location.pathname + location.search + location.hash
    return getResourceType(fullUrl, id) || undefined
  }, [location.pathname, location.search, location.hash, vehicleUUID, workorderUUID, resourceID])

  const vinMasked = () => {
    if (!hasVinMaskingFeature || isPortal) {
      return false
    }
    return isVinMasked(vin)
  }

  const { mutateAsync: unmaskVinMutation, isLoading: isUnmasking } = useMutation(
    async () => {
      if (!vehicleUUID) {
        throw new Error('vehicleUUID is required for unmaskVin')
      }
      const response = await dispatch(showVehicleVin(vehicleUUID, workorderUUID, resourceInfo))
      return response.Data.Vin
    },
    {
      onSuccess: (unmaskedVinData: string) => {
        setUnmaskedVin(unmaskedVinData)
      }
    }
  )

  const unmaskVin = React.useCallback(
    async (vin?: string) => {
      const needUnmasking = vin ? isVinMasked(vin) : vinMasked()
      if (!needUnmasking) {
        console.warn('Attempted to unmask a VIN that is not masked')
        return
      }

      try {
        await unmaskVinMutation()
      } catch (error) {
        console.error('Failed to unmask VIN:', error)
        throw error
      }
    },
    [vinMasked, unmaskVinMutation]
  )

  const unmaskVinByRegistration = React.useCallback(
    async (reg: string, provider = VehicleProvider.Mdm): Promise<string | null> => {
      try {
        const result = await dispatch(showVehicleVinByRegistration(reg, provider, vehicleUUID, resourceInfo))
        const vehicleData = result?.Data
        if (vehicleData && Array.isArray(vehicleData) && vehicleData.length > 0 && vehicleData[0]?.Vin) {
          const unmaskedVinValue = vehicleData[0].Vin
          setUnmaskedVin(unmaskedVinValue)
          return unmaskedVinValue
        }
        return null
      } catch (error) {
        console.error('Failed to unmask VIN by registration:', error)
        throw error
      }
    },
    [dispatch]
  )

  const resetUnmaskedVin = React.useCallback(() => {
    setUnmaskedVin(null)
  }, [])

  const UnmaskDialogWithProps = React.useCallback(
    (props: Omit<UnmaskConfirmationDialogProps, 'vehicleUUID' | 'workorderUUID' | 'registration'>) => (
      <VinUnmaskConfirmationDialog
        {...props}
        vehicleUUID={vehicleUUID}
        workorderUUID={workorderUUID}
        registration={registration}
        resourceInfo={resourceInfo}
      />
    ),
    [vehicleUUID, workorderUUID, registration, resourceInfo]
  )

  return {
    vinMasked,
    unmaskVin,
    unmaskVinByRegistration,
    isUnmasking,
    unmaskedVin,
    UnmaskConfirmationDialog: UnmaskDialogWithProps,
    vinMaskingEnabled: hasVinMaskingFeature && !isPortal,
    isVinMasked,
    resetUnmaskedVin,
    resourceType: resourceInfo?.Type,
    resourceID: resourceInfo?.Id
  }
}
